import type { CardCollection, EditableSymbol } from '../shared/collections';
import { collectionSymbolCount } from '../shared/collections';
import { catalogAssets, materializeAsset, type CatalogAsset } from './asset-catalog';
import { getLocale, t } from './i18n';
import { escape, icon } from './ui';

export function createAssetPicker(collections: () => readonly CardCollection[], onAdd: (id: string, symbols: EditableSymbol[]) => void) {
  const dialog = document.createElement('dialog');
  dialog.className = 'game-dialog library-dialog asset-picker';
  dialog.setAttribute('aria-labelledby', 'asset-picker-title');
  const tr = (key: string, vars?: Record<string, string | number>) => escape(t(key, vars));
  dialog.innerHTML = `<div class="asset-picker-inner"><header><div><h2 id="asset-picker-title">${tr('La réserve d’images')}</h2><p>${tr('Pioche dans tous les jeux.')}</p></div><button type="button" class="icon-button" data-assets-close aria-label="${tr('Fermer')}">${icon('close',20)}</button></header><div class="asset-filters"><label>${tr('Rechercher')}<input type="search" placeholder="${tr('Chercher une image…')}" maxlength="80"></label><label>${tr('Jeu d’origine')}<select></select></label></div><div class="asset-selection-tools"><button type="button" data-assets-fill></button><button type="button" data-assets-clear>${tr('Tout désélectionner')}</button></div><div class="asset-grid"></div><p class="asset-empty" hidden>${tr('Aucune image trouvée.')}</p><footer><div><strong class="asset-selection-count"></strong><span class="asset-picker-status" role="status"></span></div><button type="button" class="button primary" data-assets-add>${icon('plus',20)}<span>${tr('Ajouter au jeu')}</span></button></footer></div>`;
  document.body.append(dialog);
  const input = dialog.querySelector<HTMLInputElement>('input')!;
  const filter = dialog.querySelector<HTMLSelectElement>('select')!;
  const grid = dialog.querySelector<HTMLElement>('.asset-grid')!;
  const count = dialog.querySelector<HTMLElement>('.asset-selection-count')!;
  const status = dialog.querySelector<HTMLElement>('.asset-picker-status')!;
  const add = dialog.querySelector<HTMLButtonElement>('[data-assets-add]')!;
  const fill = dialog.querySelector<HTMLButtonElement>('[data-assets-fill]')!;
  const clear = dialog.querySelector<HTMLButtonElement>('[data-assets-clear]')!;
  let assets: CatalogAsset[] = [], visible: CatalogAsset[] = [];
  const chosen = new Set<string>();
  let currentId = '', capacity = 0, generation = 0, destroyed = false, busy = false;
  let existing = new Set<string>(), existingKeys = new Set<string>();
  const alreadyUsed = (asset: CatalogAsset) => existing.has(asset.image) || existingKeys.has(asset.key);
  const fold = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu,'').toLocaleLowerCase().replace(/œ/g,'oe').replace(/æ/g,'ae');
  function render() {
    const query = fold(input.value.trim());
    visible = assets.filter(asset => (!filter.value || asset.origins.some(origin => origin.id === filter.value)) && fold(asset.name).includes(query));
    grid.innerHTML = visible.map(asset => {
      const selected = chosen.has(asset.key), used = alreadyUsed(asset);
      const disabled = busy || used || (!selected && chosen.size >= capacity);
      return `<button type="button" class="asset-choice ${selected ? 'is-chosen' : ''}" data-asset-key="${escape(asset.key)}" aria-pressed="${selected}" ${disabled ? 'disabled' : ''}><img src="${escape(asset.thumbnail)}" alt="" loading="lazy"><span>${escape(asset.name)}</span>${selected || used ? `<b aria-hidden="true">${icon('check',15)}</b>` : ''}${used ? `<small>${tr('Déjà dans le jeu')}</small>` : ''}</button>`;
    }).join('');
    dialog.querySelector<HTMLElement>('.asset-empty')!.hidden = visible.length !== 0;
    count.textContent = t('{count} / {total} images choisies', {count:chosen.size,total:capacity});
    const available = Math.min(capacity - chosen.size, visible.filter(asset => !alreadyUsed(asset) && !chosen.has(asset.key)).length);
    fill.textContent = t('Sélectionner {count} images', {count:available});
    fill.disabled = busy || available === 0;
    clear.disabled = busy || chosen.size === 0;
    add.disabled = busy || chosen.size === 0;
    input.disabled = filter.disabled = busy;
  }
  function close() { generation++; busy = false; dialog.close(); }
  dialog.querySelector('[data-assets-close]')!.addEventListener('click', close);
  dialog.addEventListener('close', () => { generation++; busy = false; });
  input.addEventListener('input', render);
  filter.addEventListener('change', render);
  grid.addEventListener('click', event => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-asset-key]');
    if (!button || button.disabled || busy) return;
    const key = button.dataset.assetKey!;
    if (chosen.has(key)) chosen.delete(key); else if (chosen.size < capacity) chosen.add(key);
    render();
    grid.querySelector<HTMLButtonElement>(`[data-asset-key="${CSS.escape(key)}"]`)?.focus({preventScroll:true});
  });
  clear.addEventListener('click', () => { chosen.clear(); render(); });
  fill.addEventListener('click', () => {
    for (const asset of visible) {
      if (chosen.size === capacity) break;
      if (!alreadyUsed(asset)) chosen.add(asset.key);
    }
    render();
  });
  add.addEventListener('click', async () => {
    if (busy || !chosen.size) return;
    const version = ++generation;
    const id = currentId;
    const selected = assets.filter(asset => chosen.has(asset.key));
    busy = true; render();
    try {
      const symbols: EditableSymbol[] = [];
      for (const asset of selected) {
        symbols.push(await materializeAsset(asset));
        if (destroyed || version !== generation) return;
        status.textContent = t('Préparation des images : {current} / {total}', {current:symbols.length,total:selected.length});
      }
      onAdd(id, symbols);
      close();
    } catch(error) { status.textContent = t(error instanceof Error ? error.message : 'Impossible de modifier ce jeu.'); }
    finally { if (!destroyed && version === generation) { busy = false; render(); } }
  });
  return {
    open(game: CardCollection) {
      generation++; busy = false; currentId = game.id;
      capacity = collectionSymbolCount(game.order) - game.symbols.length;
      existing = new Set(game.symbols.map(symbol => symbol.image));
      existingKeys = new Set(game.symbols.map(symbol => symbol.assetKey).filter((key): key is string => !!key));
      chosen.clear(); input.value = ''; status.textContent = '';
      assets = catalogAssets(collections(), getLocale());
      const origins = new Map(assets.flatMap(asset => asset.origins.map(origin => [origin.id, origin.name] as const)));
      filter.innerHTML = `<option value="">${tr('Tous les jeux')}</option>` + [...origins].map(([id,name]) => `<option value="${escape(id)}">${escape(name)}</option>`).join('');
      filter.value = ''; render(); dialog.showModal(); if (matchMedia('(min-width: 640px)').matches) input.focus();
    },
    close,
    destroy() { destroyed = true; generation++; dialog.remove(); },
  };
}
